'use client';

import React, {useCallback, useEffect, useState} from 'react';
import DatePicker, {ReactDatePickerCustomHeaderProps, registerLocale, setDefaultLocale,} from 'react-datepicker';
import {de} from 'date-fns/locale/de';
import 'react-datepicker/dist/react-datepicker.css';
import {format, isWithinInterval} from 'date-fns';
import {toZonedTime} from 'date-fns-tz';
import {AvailableDate} from '@/app/api/types';
import {ChevronLeft, ChevronRight} from 'flowbite-react-icons/outline'

registerLocale('de', de);
setDefaultLocale('de');

interface CustomDatePickerProps {
  selectedDate: Date | null;
  onChange: (date: Date | null) => void;
  availableDates: AvailableDate[];
  timezone: string;
  minDate: Date;
  maxDate: Date;
  isDateDisabled: (date: Date) => boolean;
  calendarClassName?: string;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
                                                             selectedDate,
                                                             onChange,
                                                             availableDates,
                                                             timezone,
                                                             minDate,
                                                             maxDate,
                                                             isDateDisabled,
                                                             calendarClassName,
                                                           }) => {
  // Simplified availability check
  const isDateAvailable = useCallback(
    (date: Date) => {
      const zonedDate = toZonedTime(date, timezone);
      const dateString = format(zonedDate, 'yyyy-MM-dd');

      // Find matching dates by comparing the date portion only
      const matchingDate = availableDates.find(ad =>
        ad.date.split('T')[0] === dateString
      );

      return !!matchingDate && Array.isArray(matchingDate.slots) && matchingDate.slots.length > 0;
    },
    [availableDates, timezone]
  );

  // This function determines if a date is clickable
  const filterDate = useCallback(
    (date: Date) =>
      isWithinInterval(date, { start: minDate, end: maxDate }) &&
      isDateAvailable(date) &&
      !isDateDisabled(date),
    [isDateAvailable, isDateDisabled, minDate, maxDate]
  );

  const handleDateChange = (date: Date | null) => {
    onChange(date);
  };

  const renderCustomHeader = ({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }: ReactDatePickerCustomHeaderProps) => (
    <div className="flex items-center justify-between px-2 py-1" data-analytics-id="funnel-product-dateSelector">
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className="p-1"
      >
        <ChevronLeft className="w-8 h-8 text-[#924D6F]" />
      </button>
      <span className="mx-2 font-semibold">
      {format(date, 'MMMM yyyy', { locale: de })}
    </span>
      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className="p-1"
      >
        <ChevronRight className="w-6 h-6 text-[#924D6F]" />
      </button>
    </div>
  );

  return (
    <DatePicker
      selected={selectedDate}
      onChange={handleDateChange}
      filterDate={filterDate}
      inline
      minDate={minDate}
      maxDate={maxDate}
      dayClassName={(date) => {
        // Only add the available-day class if the date would pass the filterDate function
        // This ensures perfect alignment between clickable dates and styled dates
        const isClickable = filterDate(date);

        return isClickable ? 'available-day' : '';
      }}
      renderCustomHeader={renderCustomHeader}
      calendarClassName={calendarClassName}
      locale="de"
      dateFormat="dd.MM.yyyy"
      formatWeekDay={customWeekdayFormat}
    />
  );
};




const customWeekdayFormat = (nameOfDay: string) => {
  const abbreviations: { [key: string]: string } = {
    Montag: 'Mo',
    Dienstag: 'Di',
    Mittwoch: 'Mi',
    Donnerstag: 'Do',
    Freitag: 'Fr',
    Samstag: 'Sa',
    Sonntag: 'So',
  };
  return abbreviations[nameOfDay] || nameOfDay.substr(0, 2);
};

export default CustomDatePicker;
